/*
 * What the shell's navigation offers, as data: one pure function from what this person may reach in
 * the workspace on screen to the rows the rail and the phone menu draw (master-inbox plan, step 2:
 * docs/2026-10-17-master-inbox-in-the-existing-ui-plan.md, "How the reply-agent views and the client
 * inbox share one shell"). The four audiences are one table, tested as one
 * (`test/shell-nav.test.ts`), and the browser only draws it: the API refuses whatever a row would
 * have led to for someone who may not reach it (`src/route-access.ts`).
 *
 * The master inbox's own place, the box, the label, the ready "Needs reply" view or a saved view, is
 * in its address, so a reload or a shared link keeps it and the rail's rows are plain links. Inbox
 * (the inbox box) is home, the workspace's bare address; Needs reply is `?status=needs_reply`
 * (LEA-485, docs/2026-11-02-lea-485-inbox-home-plan.md: while replies are answered in Bison, a home
 * of what waits on us hid the newest replies); Meeting requests, the Interested replies asking to meet
 * that still need one, is `?status=meeting` (LEA-494). Whose threads, the owner scope, is its own part beside
 * the place (`?assigned=`), so any place can be narrowed to one person
 * (docs/2026-10-27-inbox-rail-scope-and-counts-plan.md, step 3).
 */
import type { InboxLabelCode } from "../src/inbox-label.ts";
import type { InboxAssignee, InboxCounts, InboxLabelView } from "../src/inbox-v2/contract.ts";
import type { PlainMoreFolder } from "../src/message-index/contract.ts";
import type { WorkspaceAccess } from "../src/route-access.ts";
import { LABEL_VIEWS } from "./inbox-v2/labels.ts";
import {
  answersPath,
  campaignsPath,
  inboxPath,
  mailboxesPath,
  settingsPath,
} from "./workspace-context.tsx";

export type MailBox = "inbox" | "snoozed" | "sent" | "done" | "trash";

/** Where in the master inbox the list is: a box, one label of the inbox, a ready or a saved view. */
export type MailChoice =
  | { readonly kind: "box"; readonly box: MailBox }
  | { readonly kind: "label"; readonly label: InboxLabelView }
  | { readonly kind: "meeting" }
  | { readonly kind: "needs_reply" }
  | { readonly kind: "follow_up" }
  /** The person's own saved drafts (LEA-513): conversations, whatever box they sit in. */
  | { readonly kind: "drafts" }
  | { readonly kind: "view"; readonly id: string };

/**
 * Whose threads the list holds, beside its place: `me`, `none` (nobody) or one person's user id
 * (card A4); null is everyone. Assignment is a filter, never a permission.
 */
export type Owner = string | null;

/** Home: what the workspace's own address opens, and where the brand goes. */
const HOME: MailChoice = { kind: "box", box: "inbox" };

const BOXES: readonly MailBox[] = ["inbox", "snoozed", "sent", "done", "trash"];

/** The page every shell frame can be on. `inbox` is the reply agent's; `mail` the master inbox. */
export type ShellPage =
  | "inbox"
  | "mail"
  | "settings"
  | "answers"
  | "campaigns"
  | "campaign"
  | "mailboxes"
  | "account";

/** The choice an address names; anything it does not recognise is home, Inbox. */
export function mailChoiceFrom(search: string): MailChoice {
  const params = new URLSearchParams(search);
  const view = params.get("view");
  if (view) return { kind: "view", id: view };
  if (params.get("status") === "meeting") return { kind: "meeting" };
  if (params.get("status") === "needs_reply") return { kind: "needs_reply" };
  if (params.get("status") === "follow_up") return { kind: "follow_up" };
  if (params.get("box") === "drafts") return { kind: "drafts" };
  const label = LABEL_VIEWS.find((candidate) => candidate.view === params.get("label"))?.view;
  if (label) return { kind: "label", label };
  const box = params.get("box");
  const known = BOXES.find((candidate) => candidate === box);
  return known ? { kind: "box", box: known } : HOME;
}

/** The owner an address narrows to; null, everyone, without one. */
export function ownerFrom(search: string): Owner {
  return new URLSearchParams(search).get("assigned") || null;
}

function mailSearch(choice: MailChoice): string {
  switch (choice.kind) {
    case "box":
      return choice.box === "inbox" ? "" : `?box=${choice.box}`;
    case "label":
      return `?label=${encodeURIComponent(choice.label)}`;
    case "meeting":
      return "?status=meeting";
    case "needs_reply":
      return "?status=needs_reply";
    case "follow_up":
      return "?status=follow_up";
    case "drafts":
      return "?box=drafts";
    case "view":
      return `?view=${encodeURIComponent(choice.id)}`;
  }
}

/**
 * The master inbox's address for a choice and an owner: the workspace's own address (step 10), its
 * list in the query.
 */
export function mailHref(workspaceId: string, choice: MailChoice = HOME, owner: Owner = null) {
  const place = mailSearch(choice);
  const scope = owner ? `${place ? "&" : "?"}assigned=${encodeURIComponent(owner)}` : "";
  return `${inboxPath(workspaceId)}${place}${scope}`;
}

/** A choice's query as addresses wrote it before Needs reply became home. */
function storedSearch(choice: MailChoice): string {
  if (choice.kind === "needs_reply") return "?status=needs_reply";
  return choice.kind === "box" && choice.box === "inbox" ? "" : mailSearch(choice);
}

/**
 * The name a view's chosen order is kept under: the master inbox's address before step 10, kept as
 * it was so the orders reps chose then still apply.
 */
export const viewKey = (workspaceId: string, choice: MailChoice): string =>
  `${inboxPath(workspaceId)}/inbox${storedSearch(choice)}`;

export const sameChoice = (a: MailChoice, b: MailChoice): boolean =>
  mailSearch(a) === mailSearch(b);

/*
 * The More group (docs/2026-10-19-inbox-sub-inboxes-plan.md, "Where they sit in the UI"): mail
 * nobody must answer, reachable and out of the way, outside every work count. A folder is not a
 * `MailChoice`: it has its own address (`?folder=`) and page, so nothing the inbox counts, selects or
 * acts on can reach it. Bounces came first (LEA-311); LEA-314 added the other four, each addressed by
 * the path segment its API routes use.
 */
export type MoreFolder = "bounces" | PlainMoreFolder;
export type FolderPart = "hard" | "soft";
export type FolderChoice = { readonly folder: MoreFolder; readonly part: FolderPart | null };

/**
 * The More group's folders in their default order, as data, so a person's own order can replace it
 * (LEA-312). Each lists its parts, which its page draws as tabs: one rail row per folder.
 */
const MORE_FOLDERS: readonly {
  readonly folder: MoreFolder;
  readonly label: string;
  readonly parts: readonly { readonly part: FolderPart; readonly label: string }[];
}[] = [
  { folder: "auto-replies", label: "Auto-replies", parts: [] },
  {
    folder: "bounces",
    label: "Bounces",
    parts: [
      { part: "hard", label: "Hard" },
      { part: "soft", label: "Soft" },
    ],
  },
  { folder: "other-campaigns", label: "Other campaigns", parts: [] },
  { folder: "spam", label: "Spam", parts: [] },
  { folder: "untracked", label: "Untracked (in Bison)", parts: [] },
];

/** The folder an address names, or null for the inbox's own places. */
export function folderChoiceFrom(search: string): FolderChoice | null {
  const params = new URLSearchParams(search);
  const folder = MORE_FOLDERS.find((candidate) => candidate.folder === params.get("folder"));
  if (!folder) return null;
  const part = folder.parts.find((candidate) => candidate.part === params.get("part"))?.part;
  return { folder: folder.folder, part: part ?? null };
}

export const folderHref = (workspaceId: string, choice: FolderChoice): string =>
  `${inboxPath(workspaceId)}?folder=${choice.folder}${choice.part ? `&part=${choice.part}` : ""}`;

/** What the API says this person may reach here; undefined while it is being asked. */
export type NavAccess = WorkspaceAccess | undefined;

type NavIconName =
  | "inbox"
  | "meeting"
  | "needs-reply"
  | "follow-up"
  | "snoozed"
  | "drafts"
  | "sent"
  | "done"
  | "trash"
  | MoreFolder
  | "more"
  | "all-mail"
  | "view"
  | "agent"
  | "campaigns"
  | "mailboxes"
  | "answers"
  | "settings";

export type NavRow = {
  readonly key: string;
  readonly href: string;
  readonly label: string;
  /** A glyph, or the label's tone drawn as a dot. */
  /** An icon, or a label view's key: its label, the rail's dot being the row's legend. */
  readonly icon: NavIconName | { readonly label: InboxLabelCode | null };
  /** A total to show beside the label, 0 included; null where the row has none. */
  readonly count: number | null;
  /** The row has a total on its way: the counts have not answered yet, so its slot holds a bar. */
  readonly counting: boolean;
  /** The counts could not be read: the slot says unknown, which is not the same as none. */
  readonly unknown: boolean;
  readonly current: boolean;
  /** A label view: drawn in the Labels group, smaller, and only on the labelled rail. */
  readonly sub: boolean;
  /** False where the page has its own deeper marker (settings has its section nav). */
  readonly marked: boolean;
  /** A saved view's id, for its Rename and Delete; absent on every other row. */
  readonly viewId?: string;
};

/** One choice of the owner scope: what it says, and the same place narrowed to it. */
type ScopeChoice = { readonly value: string; readonly label: string; readonly href: string };

/**
 * The owner scope (step 3): Everyone, Mine, Unassigned, then each colleague by name. One control,
 * whatever the team's size, that narrows every row and count beside it.
 */
export type ScopeNav = {
  /** `everyone`, `me`, `none` or a colleague's user id. */
  readonly value: string;
  /** What the control shows: the choice's own words. */
  readonly label: string;
  readonly fixed: readonly ScopeChoice[];
  readonly people: readonly ScopeChoice[];
};

export type ShellNav = {
  /** Where the brand goes: the master inbox's home, Inbox, or else the reply agent's inbox. */
  readonly home: string;
  /** Whose threads the rows hold; null until the workspace assigns anything (and none is chosen). */
  readonly scope: ScopeNav | null;
  /** The master inbox's rows; empty for a workspace without the inbox. */
  readonly mail: readonly NavRow[];
  /** This person's saved views of the master inbox. */
  readonly views: readonly NavRow[];
  /** The More group's folders, without counts; empty without the inbox. */
  readonly more: readonly NavRow[];
  /**
   * The app rail's sections (LEA-501): Inbox, with the work waiting on us as its total, then the
   * reply agent's places and the workspace's own pages. Settings sits apart, at the rail's foot.
   */
  readonly sections: readonly NavRow[];
  readonly settings: NavRow | null;
};

/** The Inbox section's key: current on every page of the master inbox, its rows in the panel. */
export const INBOX_SECTION = "section:inbox";

/**
 * The work waiting on us: Needs reply's total, with Meeting requests' added where the counts carry
 * it; null while Needs reply has none to read. The browser tab and the rail's Inbox say the same.
 */
export function workTotal(mail: readonly NavRow[]): number | null {
  const count = (key: string) => mail.find((row) => row.key === key)?.count ?? null;
  const needsReply = count("needs_reply");
  return needsReply === null ? null : needsReply + (count("meeting") ?? 0);
}

export type ShellNavInput = {
  readonly mailbox?: boolean;
  readonly workspaceId: string;
  /** The workspace list's word on the inbox here: the API's own rule, with no extra request. */
  readonly inbox: boolean;
  readonly access: NavAccess;
  readonly counts: InboxCounts | undefined;
  /** The counts' first answer is on its way (not refused, not switched off). */
  readonly countsLoading: boolean;
  /** The counts failed and none were read before (W3, F15). */
  readonly countsFailed?: boolean;
  readonly views: readonly { readonly id: string; readonly name: string }[];
  readonly page: ShellPage;
  /** The master inbox's place, read from the address; null on any other page or in a folder. */
  readonly choice: MailChoice | null;
  /** The More folder on screen, read from the address; null outside one. */
  readonly folder?: FolderChoice | null;
  /** The owner the address narrows to; null or absent is everyone. */
  readonly owner?: Owner;
  /**
   * Assignment here (card A4), absent where it is off: whether some open thread is assigned, which
   * is when the scope is offered, the viewer and who a thread may be assigned to.
   */
  readonly assignment?:
    | {
        readonly inUse: boolean;
        readonly you: string | null;
        readonly people: readonly InboxAssignee[];
      }
    | undefined;
};

const row = (
  fields: Pick<NavRow, "key" | "href" | "label" | "icon" | "current"> & Partial<NavRow>,
): NavRow => ({
  count: null,
  counting: false,
  unknown: false,
  sub: false,
  marked: true,
  ...fields,
});

/** How a row reads its total from the counts; undefined where the counts leave it out. */
type Total = (counts: InboxCounts) => number | undefined;

/**
 * The fixed scopes. "Mine and unassigned" is what the workspace switcher counts ("47 for you", Dawid's
 * rule of 2026-10-20), so its number is a list here (LEA-501); the switcher opens a workspace on it.
 */
export const MINE_OR_UNASSIGNED = "me-or-none";
const FIXED_SCOPES: readonly { readonly value: string; readonly label: string }[] = [
  { value: "everyone", label: "Everyone" },
  { value: "me", label: "Mine" },
  { value: "none", label: "Unassigned" },
  { value: MINE_OR_UNASSIGNED, label: "Mine and unassigned" },
];

/**
 * The Showing menu's choices, one tree wherever it is drawn (the panel's head, the folded list's
 * heading): the fixed scopes, then colleagues one level down under "Someone else", so twenty of
 * them never push the fixed ones away.
 */
export function scopeOptions(scope: ScopeNav) {
  return [
    ...scope.fixed.map(({ value, label }) => ({ value, label })),
    ...(scope.people.length > 0
      ? [
          {
            key: "people",
            label: "Someone else",
            submenu: scope.people.map(({ value, label }) => ({ value, label })),
          },
        ]
      : []),
  ];
}

/**
 * The owner scope, offered once the workspace assigns anything, or while an address already narrows
 * to someone, so the way back to everyone stays on screen. Each choice keeps the place on screen;
 * from a folder or another page it lands on home.
 */
function scopeNav(input: ShellNavInput): ScopeNav | null {
  const owner = input.owner ?? null;
  const assignment = input.assignment;
  if (!input.inbox || !assignment || (!assignment.inUse && owner === null)) return null;
  const place = input.page === "mail" && input.choice ? input.choice : HOME;
  const choice = (value: string, label: string): ScopeChoice => ({
    value,
    label,
    href: mailHref(input.workspaceId, place, value === "everyone" ? null : value),
  });
  const people = assignment.people
    .filter((person) => person.userId !== assignment.you)
    .map((person) => choice(person.userId, person.name));
  const fixed = FIXED_SCOPES.map(({ value, label }) => choice(value, label));
  const value = owner ?? "everyone";
  const label =
    [...fixed, ...people].find((candidate) => candidate.value === value)?.label ?? "Someone";
  return { value, label, fixed, people };
}

function mailRows(input: ShellNavInput): NavRow[] {
  const { workspaceId: id, counts, choice } = input;
  const owner = input.owner ?? null;
  const on = (candidate: MailChoice) =>
    input.page === "mail" && choice !== null && sameChoice(choice, candidate);
  // A row with a total shows it, or a bar in its place while the counts are on their way.
  const total = (read: Total) => ({
    count: (counts && read(counts)) ?? null,
    counting: input.countsLoading,
    unknown: input.countsFailed === true && counts === undefined,
  });
  const box = (box: MailBox, label: string, icon: NavIconName, read?: Total) => {
    const target: MailChoice = { kind: "box", box };
    const counted = read ? total(read) : {};
    return row({
      key: box,
      href: mailHref(id, target, owner),
      label,
      icon,
      ...counted,
      current: on(target),
    });
  };
  // Inbox first, home: every open reply, with no total, since "every open thread" is no amount of
  // work; the list's heading shows it. Then the work, each row with its total and none in two of
  // them: Meeting requests (Interested replies asking to meet, LEA-494), Needs reply (the rest of
  // what waits on our side) and Follow-up due (leads we answered gone quiet for 3 to 10 business
  // days, LEA-486). The label views follow as a group of their own, each every open thread with its
  // label and no total (LEA-494 reverses LEA-485's labels directly under Inbox: Dawid, 2026-11-03,
  // "Needs reply is relaly hard to notice"), then the boxes.
  const MEETING: MailChoice = { kind: "meeting" };
  const NEEDS_REPLY: MailChoice = { kind: "needs_reply" };
  const FOLLOW_UP: MailChoice = { kind: "follow_up" };
  const DRAFTS: MailChoice = { kind: "drafts" };
  return [
    box("inbox", "Inbox", "inbox"),
    row({
      key: "meeting",
      href: mailHref(id, MEETING, owner),
      label: "Meeting requests",
      icon: "meeting",
      ...total((c) => c.meeting),
      current: on(MEETING),
    }),
    row({
      key: "needs_reply",
      href: mailHref(id, NEEDS_REPLY, owner),
      label: "Needs reply",
      icon: "needs-reply",
      ...total((c) => c.needs_reply),
      current: on(NEEDS_REPLY),
    }),
    row({
      key: "follow_up",
      href: mailHref(id, FOLLOW_UP, owner),
      label: "Follow-up due",
      icon: "follow-up",
      ...total((c) => c.follow_up),
      current: on(FOLLOW_UP),
    }),
    ...LABEL_VIEWS.map(({ view, name }) => {
      const target: MailChoice = { kind: "label", label: view };
      return row({
        key: `label:${view}`,
        href: mailHref(id, target, owner),
        label: name,
        icon: { label: view === "none" ? null : view },
        current: on(target),
        sub: true,
      });
    }),
    box("snoozed", "Snoozed", "snoozed", (c) => c.snoozed),
    // Drafts (LEA-513) shows only where the counts name it: the server says so once it is released.
    ...(counts?.drafts === undefined
      ? []
      : [
          row({
            key: "drafts",
            href: mailHref(id, DRAFTS, owner),
            label: "Drafts",
            icon: "drafts",
            ...total((c) => c.drafts),
            current: on(DRAFTS),
          }),
        ]),
    box("sent", "Sent", "sent"),
    box("done", "Done", "done", (c) => c.done),
    // Trash (LEA-457) counts as Done does: what is in it, out of every other row's total.
    box("trash", "Trash", "trash", (c) => c.trash),
  ];
}

/** The mail rows' groups, as the rail and the phone menu part them; `label` heads a group. */
type MailGroup = {
  readonly key: string;
  readonly label: string | null;
  readonly rows: readonly NavRow[];
};

/** What heads the label views' group: they are no longer the rows under Inbox (LEA-494). */
const LABEL_GROUP = "Labels";

/**
 * The mail rows in their groups, in order: Inbox and the work, the label views under "Labels", then
 * the boxes. A run of label rows is one group; whatever follows it starts the next.
 */
export function mailGroups(rows: readonly NavRow[]): MailGroup[] {
  const groups: { key: string; label: string | null; rows: NavRow[] }[] = [];
  for (const candidate of rows) {
    const label = candidate.sub ? LABEL_GROUP : null;
    const last = groups.at(-1);
    if (last && last.label === label) last.rows.push(candidate);
    else groups.push({ key: `group:${candidate.key}`, label, rows: [candidate] });
  }
  return groups;
}

/*
 * Settings is available to everyone. The reply agent's rows are drawn only once the API has said this
 * person holds the agent, so a client's rep never sees one, not even while the answer loads; the
 * inbox itself is the mail rows, so there is no "Reply agent" row (step 10). A workspace without the
 * inbox has no client reps (`replyAgentAllowed`), and keeps the rows it always had while the answer
 * loads.
 */
function pageRows(input: ShellNavInput): { pages: NavRow[]; settings: NavRow | null } {
  // The master inbox's own Inbox is the rail's Inbox section, built with its counts in `shellNav`.
  const id = input.workspaceId;
  const agent = input.inbox
    ? input.access?.replyAgent === true
    : input.access?.replyAgent !== false;
  const page = (key: NavIconName, href: string, label: string, current: boolean) =>
    row({ key, href, label, icon: key, current });
  const first = input.inbox ? [] : [page("inbox", inboxPath(id), "Inbox", input.page === "inbox")];
  return {
    pages: [
      ...first,
      ...(agent
        ? [
            page(
              "campaigns",
              campaignsPath(id),
              "Campaigns",
              input.page === "campaigns" || input.page === "campaign",
            ),
          ]
        : []),
      ...(input.access?.workspaceAdmin === true
        ? [page("mailboxes", mailboxesPath(id), "Mailboxes", input.page === "mailboxes")]
        : []),
      ...(agent
        ? [page("answers", answersPath(id), "Standing answers", input.page === "answers")]
        : []),
    ],
    settings: row({
      key: "settings",
      href: settingsPath(id),
      label: "Settings",
      icon: "settings",
      // The account's own pages (Connected apps) are listed under Settings, so it is current there
      // too; Settings' column beside them carries the stroke, the deeper level (LEA-501).
      current: input.page === "settings" || input.page === "account",
      marked: false,
    }),
  };
}

/**
 * The More group's rows, one per folder and current on any of its parts. No totals: not work.
 * All mail (LEA-367) closes the group: a live Bison read kept as a fallback until every message is
 * proven stored, so it stays out of the way of Needs reply, home and the first row (LEA-456 plan).
 */
function moreRows(input: ShellNavInput): NavRow[] {
  const on = input.page === "mail" ? (input.folder ?? null) : null;
  const folders = MORE_FOLDERS.map(({ folder, label }) =>
    row({
      key: `folder:${folder}`,
      href: folderHref(input.workspaceId, { folder, part: null }),
      label,
      icon: folder,
      current: on?.folder === folder,
    }),
  );
  const allMail = row({
    key: "all_mail",
    href: `${inboxPath(input.workspaceId)}?mailbox=all`,
    label: "All mail",
    icon: "all-mail",
    current: input.page === "mail" && input.mailbox === true,
  });
  return [...folders, allMail];
}

/**
 * The rail's Inbox where the workspace has the master inbox: the panel's Inbox, carrying the work's
 * total. Its count and its address follow the scope chosen (Mine, Unassigned, a colleague) as the
 * panel's rows do; the brand alone goes to everyone's home.
 */
function inboxSection(input: ShellNavInput, mail: readonly NavRow[]): NavRow {
  const needsReply = mail.find((row) => row.key === "needs_reply");
  return row({
    key: INBOX_SECTION,
    href: mail.find((row) => row.key === "inbox")?.href ?? mailHref(input.workspaceId),
    label: "Inbox",
    icon: "inbox",
    count: workTotal(mail),
    counting: needsReply?.counting ?? false,
    unknown: needsReply?.unknown ?? false,
    current: input.page === "mail",
  });
}

export function shellNav(input: ShellNavInput): ShellNav {
  const id = input.workspaceId;
  const { pages, settings } = pageRows(input);
  const mail = input.inbox ? mailRows(input) : [];
  const views = input.inbox
    ? input.views.map((view) => {
        const target: MailChoice = { kind: "view", id: view.id };
        return row({
          key: `view:${view.id}`,
          viewId: view.id,
          href: mailHref(id, target, input.owner ?? null),
          label: view.name,
          icon: "view",
          current:
            input.page === "mail" && input.choice !== null && sameChoice(input.choice, target),
        });
      })
    : [];
  return {
    home: input.inbox ? mailHref(id) : inboxPath(id),
    scope: input.mailbox ? null : scopeNav(input),
    mail,
    views,
    more: input.inbox ? moreRows(input) : [],
    sections: input.inbox ? [inboxSection(input, mail), ...pages] : pages,
    settings,
  };
}
